'use client';

import * as React from 'react';
import { Heading } from '@/hen-ui/atomics/Heading';
import { Link } from '@/hen-ui/atomics/Link';
import { List, ListItem } from '@/hen-ui/atomics/List';
import { Section } from '@/hen-ui/composites/Section';
import { cn } from '@/hen-ui/utils';

const TocContext = React.createContext<{
  onNavigate: (id: string) => void;
}>({ onNavigate: () => {} });

export interface TocItem {
  href: string;
  label: string;
}

export interface TableOfContentsProps {
  title?: string;
  items?: TocItem[];
  children?: React.ReactNode;
  className?: string;
}

/**
 * TableOfContents - Sidebar navigation for long-form article content
 *
 * Renders an "In this Article" style navigation with scroll-tracking.
 * The active section is highlighted with a primary-colored indicator.
 *
 * Two usage modes:
 * 1. **`items` prop** — pass an explicit array of `{ href, label }`.
 * 2. **Children** — compose with `<TableOfContents.Item>` elements.
 *
 * @example Items prop
 * ```tsx
 * <TableOfContents
 *   title="In this Article"
 *   items={[
 *     { href: '#overview', label: 'Overview' },
 *     { href: '#details', label: 'Details' },
 *   ]}
 * />
 * ```
 *
 * @example Children
 * ```tsx
 * <TableOfContents title="In this Article">
 *   <TableOfContents.Item href="#overview">Overview</TableOfContents.Item>
 *   <TableOfContents.Item href="#details">Details</TableOfContents.Item>
 * </TableOfContents>
 * ```
 */
export const TableOfContents = Object.assign(
  React.forwardRef<HTMLElement, TableOfContentsProps>(
    (
      { title = 'In this Article', items, children, className, ...props },
      ref,
    ) => {
      const [activeId, setActiveId] = React.useState<string>('');
      const [scrollProgress, setScrollProgress] = React.useState(0);
      const isNavigatingRef = React.useRef(false);
      const navTimerRef = React.useRef<
        ReturnType<typeof setTimeout> | undefined
      >(undefined);

      React.useEffect(() => {
        function handleScroll() {
          const scrollTop = window.scrollY;
          const docHeight =
            document.documentElement.scrollHeight - window.innerHeight;
          setScrollProgress(
            docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0,
          );
        }

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
      }, []);

      const allIds = React.useMemo(() => {
        if (items) {
          return items
            .map((item) => item.href.replace(/^#/, ''))
            .filter(Boolean);
        }
        const ids: string[] = [];
        React.Children.forEach(children, (child) => {
          if (
            React.isValidElement<{ href?: string }>(child) &&
            child.props.href
          ) {
            const id = child.props.href.replace(/^#/, '');
            if (id) ids.push(id);
          }
        });
        return ids;
      }, [items, children]);

      React.useEffect(() => {
        if (!allIds.length) return;

        const observer = new IntersectionObserver(
          (entries) => {
            if (isNavigatingRef.current) return;

            const visible = entries
              .filter((e) => e.isIntersecting)
              .sort(
                (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
              );

            if (visible.length > 0) {
              setActiveId(visible[0].target.id);
            }
          },
          { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
        );

        for (const id of allIds) {
          const el = document.getElementById(id);
          if (el) observer.observe(el);
        }

        return () => observer.disconnect();
      }, [allIds]);

      React.useEffect(() => {
        if (!allIds.length) return;
        const hash = window.location.hash.replace(/^#/, '');
        if (!hash || !allIds.includes(hash)) return;

        setActiveId(hash);
      }, [allIds]);

      const handleNavigate = React.useCallback((id: string) => {
        setActiveId(id);
        isNavigatingRef.current = true;
        clearTimeout(navTimerRef.current);
        navTimerRef.current = setTimeout(() => {
          isNavigatingRef.current = false;
        }, 1000);
      }, []);

      if (!allIds.length && !React.Children.count(children)) return null;

      return (
        <TocContext.Provider value={{ onNavigate: handleNavigate }}>
          <nav
            ref={ref}
            aria-label={title}
            className={cn('flex flex-col gap-sm', className)}
            {...props}
          >
            <Section
              as='div'
              padding='none'
              gap='min'
              className='h-1 rounded-full bg-neutral-100 overflow-hidden gap-0'
              role='progressbar'
              aria-valuenow={Math.round(scrollProgress * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label='Reading progress'
            >
              <Section
                as='div'
                padding='none'
                gap='min'
                className='h-full bg-primary-700 transition-[width] duration-150 ease-out gap-0'
                style={{ width: `${scrollProgress * 100}%` }}
              />
            </Section>
            {title && (
              <Heading variant='h3' className='m-0 text-lg leading-7'>
                {title}
              </Heading>
            )}
            <List bulletStyle='none' className='gap-xs p-0 m-0'>
              {items
                ? items.map((item) => (
                    <TocItemInternal
                      key={item.href}
                      href={item.href}
                      isActive={activeId === item.href.replace(/^#/, '')}
                    >
                      {item.label}
                    </TocItemInternal>
                  ))
                : React.Children.map(children, (child) => {
                    if (React.isValidElement<TableOfContentsItemProps>(child)) {
                      const href = child.props.href || '';
                      const id = href.replace(/^#/, '');
                      return React.cloneElement(child, {
                        ...child.props,
                        isActive: activeId === id,
                      });
                    }
                    return child;
                  })}
            </List>
          </nav>
        </TocContext.Provider>
      );
    },
  ),
  { Item: TableOfContentsItem },
);

TableOfContents.displayName = 'TableOfContents';

// ---------------------------------------------------------------------------
// TableOfContentsItem
// ---------------------------------------------------------------------------

export interface TableOfContentsItemProps {
  href: string;
  isActive?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export function TableOfContentsItem({
  href,
  isActive = false,
  children,
  className,
}: TableOfContentsItemProps) {
  return (
    <TocItemInternal href={href} isActive={isActive} className={className}>
      {children}
    </TocItemInternal>
  );
}

TableOfContentsItem.displayName = 'TableOfContents.Item';

// ---------------------------------------------------------------------------
// Shared internal renderer
// ---------------------------------------------------------------------------

function TocItemInternal({
  href,
  isActive,
  children,
  className,
}: {
  href: string;
  isActive: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  const { onNavigate } = React.useContext(TocContext);

  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const id = href.replace(/^#/, '');
      const el = document.getElementById(id);
      if (el) {
        e.preventDefault();
        onNavigate(id);
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState(null, '', href);
      }
    },
    [href, onNavigate],
  );

  return (
    <ListItem
      className={cn('flex items-center gap-xs !leading-none', className)}
    >
      <span
        className={cn(
          'inline-block w-2 h-2 rounded-full shrink-0 transition-colors',
          isActive ? 'bg-primary-700' : 'bg-transparent',
        )}
        aria-hidden='true'
      />
      <Link
        href={href}
        onClick={handleClick}
        underline='none'
        weight={isActive ? 'semibold' : 'regular'}
        className={cn(
          'transition-colors',
          isActive
            ? 'text-primary-700'
            : 'text-neutral-600 hover:text-neutral-900',
        )}
      >
        {children}
      </Link>
    </ListItem>
  );
}
